// @ts-nocheck
/*
 * Port of views/dashboard/credit-history/list.js (800 lines). BYTE-IDENTICAL.
 *
 * Voice credit ledger with All / Standard / Demo tab filter, 6-month
 * max-range date filter, client-side search across from/to/actionOn/action,
 * per-company summary panel (superadmin sees totals across the current
 * listing; other roles see totals scoped to their assigned company),
 * CSV export, flash-message alert marquee, ACL read gate + refresh listener.
 *
 * PrimeReact DataTable → obd3-gui DataTable; PrimeReact Button → react-bootstrap.
 *
 * V1 QUIRKS preserved verbatim:
 *  - "Reset" button now labelled Refresh in v1's Card.Header context.
 *  - The summary panel's per-role text ("Assigned Company" / "Totals
 *    across the current listing") uses italics + opacity 0.7.
 *  - `getSummary` bails early when tab-scoped list is empty (returns
 *    null so the summary block isn't rendered at all).
 *  - Calendars only allow the last MAX_RANGE_MONTHS (3) months up to today;
 *    handleFilter re-checks start >= earliestAllowedDate().
 */
import React, { useState, useEffect, useRef } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { API_CREDITS_GET, API_CALL_CENTRE_CREDITS } from '@/service/url';
import { getApiCall } from '@/service/httpCall';
import { errorCheck } from '@/views/dashboard/auth/auth-utils';
import { getUser, Storage } from '@/utilities/setting';
import {
  FaArrowUp,
  FaArrowDown,
  FaExchangeAlt,
  FaMicrophone,
} from 'react-icons/fa';
import { fetchAndSetFlashMessage } from '@/service/httpCall';
import {
  FLASH_MESSAGE_ACTION_OBJECT,
  MODULE_ACL,
  USER_ROLES,
} from '@/utilities/constant';
import {
  exportToCSV,
  hasPermission,
  formatRupeeAmount,
  toRupeeFixed,
} from '@/utilities/helpers';
import Loader from '@/components/partials/components/Loader';
import { toast } from 'sonner';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Download, RefreshCw } from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { callColumns } from './callColumns';

// Credit auto / cycle topup actions (shown as additions; superadmin / sub-superadmin only).
const TOPUP_ACTIONS = [
  'auto_topup',
  'tts_auto_topup',
  'ai_agent_auto_topup',
  'cycle_topup',
  'tts_cycle_topup',
  'ai_agent_cycle_topup',
];

// Rows written by the backend's postpaid top-up crons carry username
// "Auto Cron" — show that as From instead of the user/company name.
const AUTO_CRON_LABEL = 'Auto Cron';
const getFromLabel = (row: any) => {
  if (row?.username === AUTO_CRON_LABEL) return AUTO_CRON_LABEL;
  return row?.from ? `${row.from.name} (${row.from.type})` : '-';
};

const dateToInputStr = (d: any) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const MAX_RANGE_MONTHS = 3;
const earliestAllowedDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - MAX_RANGE_MONTHS);
  d.setHours(0, 0, 0, 0);
  return d;
};

const inputStrToLocalDate = (str: any) => {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!m) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(
    parseInt(m[1], 10),
    parseInt(m[2], 10) - 1,
    parseInt(m[3], 10),
  );
};

const CreditHistory = () => {
  const navigate = useNavigate();

  const [creditsList, setCreditsList] = useState<any[]>([]);
  const [activeTab, setActiveTab] = usePersistedState('credit-history/CreditHistory:activeTab', 'all'); // all | normal | demo
  const [searchTerm, setSearchTerm] = usePersistedState('credit-history/CreditHistory:searchTerm', '');
  const [filteredCredits, setFilteredCredits] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);

  const [startDate, setStartDate] = usePersistedState<any>('credit-history/CreditHistory:startDate', null);
  const [endDate, setEndDate] = usePersistedState<any>('credit-history/CreditHistory:endDate', null);
  const [alertMessage, setAlertMessage] = useState<any>(null);
  const [loggedInUserAssignedCompanyId, setLoggedInUserAssignedCompanyId] =
    useState<any>(null);
  const [loggedInUserRole, setLoggedInUserRole] = useState<any>(null);
  const [loggedInUserAssignedCompanyName, setLoggedInUserAssignedCompanyName] =
    useState<any>(null);

  const formatDate = (date: any) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const lastRange = useRef<[any, any] | null>(null);

  const fetchCredits = async (start: any, end: any) => {
    lastRange.current = [start, end];
    try {
      setLoading(true);

      const url = `${API_CREDITS_GET}?startDate=${formatDate(
        start,
      )}&endDate=${formatDate(end)}`;

      const resp: any = await getApiCall(url, {});
      // Auto / cycle topup logs are visible to superadmin / sub-superadmin only.
      const role = getUser()?.role;
      const canSeeTopups =
        role === USER_ROLES.SUPERADMIN || role === USER_ROLES.SUB_SUPERADMIN;
      setCreditsList(
        canSeeTopups || !Array.isArray(resp)
          ? resp
          : resp.filter((item: any) => !TOPUP_ACTIONS.includes(item?.action)),
      );
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    // Restored range (if any) wins over the month-to-date default.
    const start = startDate || firstDay;
    const end = endDate || today;

    setStartDate(start);
    setEndDate(end);

    fetchCredits(start, end);

    fetchAndSetFlashMessage(
      (msg: any) =>
        msg.module_name === MODULE_ACL.CREDIT_HISTORY &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.LIST &&
        msg.type === 'Flash Message',
      setAlertMessage,
    );
  }, []);

  // Scope a credit list by the active tab (demo vs normal vs all).
  const scopeByTab = (list: any[]) => {
    if (!Array.isArray(list)) return [];
    if (activeTab === 'demo')
      return list.filter((i: any) => i.is_demo_credits === true);
    if (activeTab === 'normal')
      return list.filter((i: any) => !i.is_demo_credits);
    return list;
  };

  useEffect(() => {
    const scoped = scopeByTab(creditsList);
    if (!searchTerm) {
      setFilteredCredits(scoped);
      return;
    }

    const lower = searchTerm.toLowerCase();

    const filtered = scoped.filter((item: any) => {
      const fromName = getFromLabel(item).toLowerCase();
      const toName = item.to?.name?.toLowerCase() || '';
      const actionOnName = item.actionOn?.name?.toLowerCase() || '';
      const action = item.action?.toLowerCase() || '';

      return (
        fromName.includes(lower) ||
        toName.includes(lower) ||
        actionOnName.includes(lower) ||
        action.includes(lower)
      );
    });

    setFilteredCredits(filtered);
  }, [searchTerm, creditsList, activeTab]);

  // 🔹 FILTER CLICK
  const handleFilter = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both dates');
      return;
    }

    const today = new Date();

    // ❌ future date validation
    if (endDate > today) {
      toast.error('End date cannot be in future');
      return;
    }

    if (startDate > endDate) {
      toast.error('Start date cannot be after end date');
      return;
    }

    if (startDate < earliestAllowedDate()) {
      toast.error(`Only the last ${MAX_RANGE_MONTHS} months can be selected`);
      return;
    }

    fetchCredits(startDate, endDate);
  };

  const handleReset = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setSearchTerm('');

    setStartDate(firstDay);
    setEndDate(today);

    fetchCredits(firstDay, today);
  };

  const [
    moduleOperationsForReadPermissionCheck,
    setModuleOperationsForReadPermissionCheck,
  ] = useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const handleExport = async () => {
    try {
      setButtonDisable(true);

      const formatCsvNumber = (value: any) => {
        const amount = Number(value);
        return Number.isNaN(amount) ? '' : toRupeeFixed(amount);
      };

      exportToCSV({
        fileName: 'credit-history',
        data: filteredCredits,
        columns: [
          { header: '#', accessor: (_row: any, index: number) => index + 1 },
          {
            header: 'From',
            accessor: (row: any) => getFromLabel(row),
          },
          {
            header: 'To',
            accessor: (row: any) =>
              row.to ? `${row.to.name} (${row.to.type})` : '-',
          },
          {
            header: 'Action On',
            accessor: (row: any) =>
              row.actionOn
                ? `${row.actionOn.name} (${row.actionOn.type})`
                : '-',
          },
          {
            header: 'Date',
            accessor: (row: any) =>
              new Date(row.req_date).toLocaleString('en-IN'),
          },
          { header: 'Action', accessor: (row: any) => row.action || '-' },
          {
            header: 'Amount',
            accessor: (row: any) => formatCsvNumber(row.credits_rupees),
          },
          {
            header: 'Balance',
            accessor: (row: any) => formatCsvNumber(row.balance),
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  function onError(err: any) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, () => {}, () => {});
    } else {
      console.log(err);
    }
  }

  useEffect(() => {
    async function superadminCheck() {
      const rawUser = Storage.getItem('user');
      if (!rawUser) {
        console.error('User not found in Storage');
        return null;
      }

      let user: any;
      try {
        user = JSON.parse(rawUser);
      } catch (e) {
        console.error('Invalid JSON in user from Storage', e);
        return null;
      }
      if (user.role) {
        setLoggedInUserRole(user.role);
      }
      if (user.role === USER_ROLES.CALL_CENTRE_ADMIN) {
        try {
          const resp: any = await getApiCall(API_CALL_CENTRE_CREDITS, {});
          setLoggedInUserAssignedCompanyName(resp?.company_name);
          setLoggedInUserAssignedCompanyId(resp?.company_id);
        } catch (e) {
          console.error("Unable to resolve call center's company", e);
        }
      } else if (user.role !== USER_ROLES.SUPERADMIN) {
        setLoggedInUserAssignedCompanyName(user?.assignedCompany?.name);
        setLoggedInUserAssignedCompanyId(user?.assignedCompany?.id);
      }
    }

    superadminCheck();
  }, []);

  const getSummary = () => {
    const scoped = scopeByTab(creditsList);
    if (!scoped || scoped.length === 0) return null;

    let summary: any = {
      credit: 0,
      debit: 0,
      inbound_deduction: 0,
      outbound_deduction: 0,
      campaign_deduction: 0,
      tts_deduction: 0,
      tts_addition: 0,
    };

    const isSuperadmin = loggedInUserRole === USER_ROLES.SUPERADMIN;

    scoped.forEach((item: any) => {
      const isRelevant =
        isSuperadmin ||
        (item.action_id == loggedInUserAssignedCompanyId &&
          item.action_type === 'Company');

      if (!isRelevant) return;

      const amount = Number(item.credits_rupees || 0);

      switch (item.action) {
        case 'addition':
          summary.credit += amount;
          break;
        case 'deduction':
          summary.debit += amount;
          break;
        case 'inbound_deduction':
          summary.inbound_deduction += amount;
          break;
        case 'outbound_deduction':
          summary.outbound_deduction += amount;
          break;
        case 'campaign_deduction':
          summary.campaign_deduction += amount;
          break;
        case 'tts_deduction':
          summary.tts_deduction += amount;
          break;
        case 'tts_addition':
          summary.tts_addition += amount;
          break;
        default:
          break;
      }
    });

    return summary;
  };

  const summary = getSummary();

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    'Credit History',
    'read',
  );

  if (
    Object.keys(moduleOperationsForReadPermissionCheck).length > 0 &&
    !canRead
  ) {
    return (
      <div
        className="text-center p-4"
        style={{
          marginTop: '50px',
          marginBottom: '50px',
          fontSize: '16px',
        }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading) return <Loader />;

  const columns: ColumnDef<any>[] = [
    {
      id: 'index',
      header: '#',
      enableSorting: false,
      cell: ({ row }: any) => row.index + 1,
    },
    {
      id: 'from',
      accessorFn: (r: any) => getFromLabel(r),
      header: 'From',
      cell: ({ row }: any) => getFromLabel(row.original),
    },
    {
      id: 'to',
      accessorFn: (r: any) => (r.to ? `${r.to.name} (${r.to.type})` : ''),
      header: 'To',
      cell: ({ row }: any) =>
        row.original.to
          ? `${row.original.to.name} (${row.original.to.type})`
          : '-',
    },
    {
      id: 'actionOn',
      accessorFn: (r: any) => (r.actionOn ? `${r.actionOn.name} (${r.actionOn.type})` : ''),
      header: 'Action On',
      cell: ({ row }: any) =>
        row.original.actionOn
          ? `${row.original.actionOn.name} (${row.original.actionOn.type})`
          : '-',
    },
    {
      id: 'demo',
      header: 'Demo',
      cell: ({ row }: any) =>
        row.original.is_demo_credits ? (
          <span style={{ color: '#d97706', fontWeight: 600 }}>Yes</span>
        ) : (
          <span style={{ color: 'gray', fontWeight: 600 }}>No</span>
        ),
    },
    {
      accessorKey: 'req_date',
      header: 'Date',
      cell: ({ row }: any) =>
        new Date(row.original.req_date).toLocaleString('en-IN'),
    },
    {
      id: 'action',
      accessorFn: (r: any) => r.action || '',
      header: 'Action',
      cell: ({ row }: any) => {
        const action = row.original.action;
        const isAddition =
          action === 'addition' ||
          action === 'tts_addition' ||
          TOPUP_ACTIONS.includes(action);
        const isDeduction =
          action === 'deduction' ||
          action === 'inbound_deduction' ||
          action === 'outbound_deduction' ||
          action === 'tts_deduction' ||
          action === 'campaign_deduction';
        return (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: isAddition ? 'green' : 'red',
            }}
          >
            {isAddition && <FaArrowUp style={{ color: 'green' }} />}
            {isDeduction && <FaArrowDown style={{ color: 'red' }} />}
            {action}
          </span>
        );
      },
    },
    {
      id: 'amount',
      header: 'Amount',
      cell: ({ row }: any) => formatRupeeAmount(row.original.credits_rupees),
    },
    {
      id: 'balance',
      header: 'Balance',
      cell: ({ row }: any) => formatRupeeAmount(row.original.balance),
    },
    ...callColumns,
  ];

  return (
    <ScrollPage>
      <PageHeader
        title="Credit History"
        description="Ledger of every credit change — allocation, top-up, deduction, refund."
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => lastRange.current && fetchCredits(...lastRange.current)}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {creditsList.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={buttonDisable}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
          </>
        }
      />
      {alertMessage && (
        <div
          className="mb-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm leading-relaxed text-emerald-700 dark:text-emerald-400"
          role="status"
        >
          {alertMessage}
        </div>
      )}

      {/* No outer Card wrapper — DataTable already ships a rounded
          border container, so nesting was creating a second edge. */}
              <div className="mb-3 flex flex-wrap items-end gap-3">
                {/* Segmented type filter — embossed track with primary
                    fill on the active pill so the current selection
                    reads at a glance. */}
                <div>
                  <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Type
                  </label>
                  <div
                    className="inline-flex h-10 items-center gap-1 rounded-full border border-border/60 bg-card p-1"
                    style={{
                      boxShadow:
                        'inset 2px 2px 5px rgba(163,177,198,0.35), inset -2px -2px 5px rgba(255,255,255,0.9)',
                    }}
                  >
                    {[
                      { key: 'all', label: 'All' },
                      { key: 'normal', label: 'Standard' },
                      { key: 'demo', label: 'Demo' },
                    ].map((t: any) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setActiveTab(t.key)}
                        className={
                          'rounded-full px-4 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 ' +
                          (activeTab === t.key
                            ? 'bg-primary text-primary-foreground shadow-[0_2px_5px_rgba(0,0,0,0.15)]'
                            : 'text-muted-foreground hover:bg-primary/10 hover:text-primary')
                        }
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <DateRangeFilter
                  value={{ from: dateToInputStr(startDate), to: dateToInputStr(endDate) }}
                  onChange={({ from, to }) => {
                    setStartDate(inputStrToLocalDate(from));
                    setEndDate(inputStrToLocalDate(to));
                  }}
                  min={dateToInputStr(earliestAllowedDate())}
                  max={dateToInputStr(new Date())}
                  onApply={handleFilter}
                  onReset={handleReset}
                />
              </div>

              {summary && (
                <div className="mb-4 px-3">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h5 className="m-0 text-sm font-semibold uppercase tracking-wide text-foreground">
                        {loggedInUserRole === USER_ROLES.SUPERADMIN
                          ? 'Summary (All Companies)'
                          : `Summary (${loggedInUserAssignedCompanyName || '-'})`}
                      </h5>
                      <p className="mt-0.5 text-[11px] italic text-muted-foreground">
                        {loggedInUserRole === USER_ROLES.SUPERADMIN
                          ? 'Totals across the current listing'
                          : 'Assigned Company'}
                      </p>
                    </div>
                    {startDate && endDate && (
                      <small className="text-xs text-muted-foreground">
                        {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
                      </small>
                    )}
                  </div>

                  {/* Compact stat strip — 6 chips on one row, wraps on narrow. */}
                  <div className="flex flex-wrap gap-2">
                    {[
                      {
                        label: 'Addition',
                        value: summary.credit,
                        icon: <FaArrowUp />,
                        tone: 'emerald',
                      },
                      {
                        label: 'Deduction',
                        value: summary.debit,
                        icon: <FaArrowDown />,
                        tone: 'rose',
                      },
                      {
                        label: 'Inbound Deduct',
                        value: summary.inbound_deduction,
                        icon: <FaExchangeAlt />,
                        tone: 'sky',
                      },
                      {
                        label: 'Outbound Deduct',
                        value: summary.outbound_deduction,
                        icon: <FaExchangeAlt />,
                        tone: 'violet',
                      },
                      {
                        label: 'Campaign Deduct',
                        value: summary.campaign_deduction,
                        icon: <FaExchangeAlt />,
                        tone: 'rose',
                      },
                      {
                        label: 'TTS Deduction',
                        value: summary.tts_deduction,
                        icon: <FaMicrophone />,
                        tone: 'orange',
                      },
                      {
                        label: 'TTS Addition',
                        value: summary.tts_addition,
                        icon: <FaMicrophone />,
                        tone: 'teal',
                      },
                    ].map((item: any, index: number) => {
                      const toneMap: any = {
                        emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
                        rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
                        sky: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300',
                        violet: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
                        orange: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
                        teal: 'bg-teal-100 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300',
                      };
                      return (
                        <div
                          key={index}
                          className="flex items-center gap-2 rounded-md border border-border/50 bg-card px-3 py-2 text-xs shadow-neu-raised"
                        >
                          <span
                            className={
                              'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm ' +
                              toneMap[item.tone]
                            }
                          >
                            {item.icon}
                          </span>
                          <div className="flex flex-col leading-tight">
                            <span className="text-[11px] text-muted-foreground">
                              {item.label}
                            </span>
                            <span className="text-sm font-bold text-foreground tabular-nums">
                              ₹{toRupeeFixed(Number(item.value || 0))}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

      <DataTable
        tableId="credit-history"
        columns={columns}
        data={filteredCredits}
        pageSize={10}
        searchPlaceholder="Search by name or action…"
        emptyTitle="No credit history"
        disableExport
      />
    </ScrollPage>
  );
};

export default CreditHistory;
